import { EyeContact } from './chunk-7QXGZ2S3.js';
import { useRef, useState, useEffect, useCallback, useMemo } from 'react';

var EMPTY_SIGNALS = {
  faceFound: false,
  yawDeg: 0,
  pitchDeg: 0,
  rollDeg: 0,
  gazeX: 0,
  gazeY: 0,
  blink: 0
};
function useEyeContact(options = {}) {
  const { autoStart = false, signalHz = 10, controllers, ...gateOptions } = options;
  const gateRef = useRef(null);
  const optionsRef = useRef(gateOptions);
  const [gate, setGate] = useState(null);
  const [state, setState] = useState("idle");
  const [reason, setReason] = useState("not-started");
  const [signals, setSignals] = useState(EMPTY_SIGNALS);
  const [awayForMs, setAwayForMs] = useState(0);
  const [recoveryProgress, setRecoveryProgress] = useState(1);
  const [fps, setFps] = useState(0);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    optionsRef.current = gateOptions;
  });
  const ensureGate = useCallback(() => {
    if (typeof window === "undefined") return null;
    if (!gateRef.current) gateRef.current = new EyeContact(optionsRef.current);
    return gateRef.current;
  }, []);
  useEffect(() => {
    const gate2 = ensureGate();
    if (!gate2) return;
    setGate(gate2);
    const lastSignalPush = { t: 0 };
    const minGap = signalHz > 0 ? 1e3 / signalHz : Infinity;
    const offs = [
      gate2.on("state", (e) => {
        setState(e.to);
        setReason(e.reason);
        if (e.to !== "away") setRecoveryProgress(1);
        if (e.to !== "error") setError(null);
      }),
      gate2.on("frame", (e) => {
        if (minGap === Infinity) return;
        if (e.t - lastSignalPush.t < minGap) return;
        lastSignalPush.t = e.t;
        setSignals(e.signals);
        setAwayForMs(e.awayForMs);
        setRecoveryProgress(e.recoveryProgress);
        setFps(gate2.snapshot.fps);
        setReason(e.reason);
      }),
      gate2.on("error", (err) => setError(err)),
      gate2.on(
        "log",
        (entry) => setLogs((prev) => prev.length > 199 ? [...prev.slice(-199), entry] : [...prev, entry])
      )
    ];
    return () => {
      for (const off of offs) off();
    };
  }, [signalHz, ensureGate]);
  useEffect(() => {
    const gate2 = ensureGate();
    if (!gate2 || !controllers?.length) return;
    const offs = controllers.map((c) => gate2.attach(c));
    return () => {
      for (const off of offs) off();
    };
  }, [controllers, ensureGate]);
  useEffect(() => {
    const gate2 = ensureGate();
    if (!gate2 || !autoStart) return;
    void gate2.start().catch(() => {
    });
  }, [autoStart, ensureGate]);
  useEffect(() => {
    return () => {
      gateRef.current?.destroy();
      gateRef.current = null;
      setGate(null);
    };
  }, []);
  const start = useCallback(async () => {
    const gate2 = ensureGate();
    if (!gate2) {
      throw new Error("EyeContact needs a browser: there is no window in this environment");
    }
    await gate2.start();
  }, [ensureGate]);
  const stop = useCallback(() => {
    gateRef.current?.stop();
    setSignals(EMPTY_SIGNALS);
    setAwayForMs(0);
    setRecoveryProgress(1);
  }, []);
  const setMode = useCallback((mode) => gateRef.current?.setMode(mode), []);
  const setThresholds = useCallback(
    (t) => gateRef.current?.setThresholds(t),
    []
  );
  const setTiming = useCallback(
    (t) => gateRef.current?.setTiming(t),
    []
  );
  const attach = useCallback(
    (c) => {
      const gate2 = ensureGate();
      if (!gate2) return () => {
      };
      return gate2.attach(c);
    },
    [ensureGate]
  );
  return useMemo(
    () => ({
      gate,
      state,
      reason,
      attentive: state === "attentive",
      running: state === "attentive" || state === "away",
      signals,
      awayForMs,
      recoveryProgress,
      fps,
      error,
      logs,
      start,
      stop,
      setMode,
      setThresholds,
      setTiming,
      attach
    }),
    [gate, state, reason, signals, awayForMs, recoveryProgress, fps, error, logs, start, stop, setMode, setThresholds, setTiming, attach]
  );
}

export { useEyeContact };
//# sourceMappingURL=react.js.map
//# sourceMappingURL=react.js.map